import { operationIds } from "@gen/app-access-config";
import { Query } from "@gen/app-query";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MuiMenu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import { alpha, useTheme } from "@mui/material/styles";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Link, useLocation, useParams } from "@tanstack/react-router";
import {
  type ElementType,
  memo,
  type MouseEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { PublicSvgIcon } from "~/components/public_svg_icon";
import {
  useActiveClient,
  useAuthOperations,
  useAuthStatus,
  useCanExecuteOperation,
} from "~/contexts/auth_query";
import { useConfig } from "~/contexts/config";

import { ClientSwitcher } from "./ClientSwitcher";
import { SystemClock } from "./SystemClock";
import { UserAvatar } from "./UserAvatar";

const MENU_COLLAPSED_STORAGE_KEY_TABLET = "menu-collapsed-tablet";
const MENU_COLLAPSED_STORAGE_KEY_DESKTOP = "menu-collapsed-desktop";

const encodeStorageValue = (value: boolean): string => {
  try {
    return btoa(JSON.stringify(value));
  } catch {
    return btoa("false");
  }
};

const decodeStorageValue = (encoded: string): boolean => {
  try {
    const decoded = atob(encoded);
    return JSON.parse(decoded);
  } catch {
    return false;
  }
};

const getStoredPreference = (
  storageKey: string,
  defaultValue: boolean,
): boolean => {
  if (typeof window === "undefined") {
    return defaultValue;
  }
  try {
    const saved = localStorage.getItem(storageKey);
    return saved ? decodeStorageValue(saved) : defaultValue;
  } catch {
    return defaultValue;
  }
};

const getInitialCollapsedState = (
  isMobile: boolean,
  isTablet: boolean,
  isDesktop: boolean,
): boolean => {
  if (isMobile) {
    return true;
  }
  if (isTablet) {
    return getStoredPreference(MENU_COLLAPSED_STORAGE_KEY_TABLET, true);
  }
  if (isDesktop) {
    return getStoredPreference(MENU_COLLAPSED_STORAGE_KEY_DESKTOP, false);
  }
  return true;
};

export const Menu = memo(function Menu() {
  const { feedbackFormUrl, contactSupportUrl } = useConfig();
  const { logout } = useAuthStatus();
  const { canExecuteAdminOperation } = useAuthOperations();
  const canExecuteOperation = useCanExecuteOperation();
  const { activeClientId: pickedClientId } = useActiveClient();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLgDown = useMediaQuery(theme.breakpoints.down("lg"));
  const location = useLocation();

  const params = useParams({ strict: false }) as { clientId?: string };
  const clientIdFromUrl = params.clientId;
  // For non-admin users, use activeClientId from context if URL doesn't have clientId.
  const clientId = clientIdFromUrl || (pickedClientId ?? undefined);
  const linkProps = clientId
    ? {
        to: "/clients/$clientId/transactions",
        params: { clientId },
      }
    : {
        to: "/admin/transactions",
      };

  const shouldShowTransactions = clientId
    ? canExecuteOperation(operationIds["core.getTransactionsByPage"])
    : canExecuteAdminOperation(
        operationIds["core_admin.getTransactionsByPage"],
      );

  const canGetClients = canExecuteAdminOperation(
    operationIds["core_admin.getClients"],
  );
  const canCreateClient = canExecuteAdminOperation(
    operationIds["core_admin.createClientWithId"],
  );
  const shouldShowClientsMenu =
    (canGetClients || canCreateClient) && !pickedClientId;

  const shouldShowAdminPayouts =
    canExecuteAdminOperation(operationIds["core_admin.getPayoutsByPage"]) &&
    !pickedClientId;

  const shouldShowClientSettings =
    canExecuteOperation(operationIds["core.getClient"]) && Boolean(clientId);

  const canGetClientFlags =
    canExecuteOperation(operationIds["core.getClientFlags"]) &&
    Boolean(clientId);

  const canGetAccountSession =
    canExecuteOperation(operationIds["core.getClientAccountSession"]) &&
    Boolean(clientId);

  const { data: clientFlagsData } = Query.useCore_getClientFlagsQuery(
    clientId ?? "",
    { enabled: canGetClientFlags },
  );

  const shouldShowAccountTab = Boolean(
    canGetAccountSession &&
    clientFlagsData?.show_account_balance &&
    shouldShowClientSettings,
  );

  const [isCollapsed, setIsCollapsed] = useState(() =>
    getInitialCollapsedState(isMobile, isTablet, isDesktop),
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setIsCollapsed(getInitialCollapsedState(isMobile, isTablet, isDesktop));
  }, [isMobile, isTablet, isDesktop]);

  useEffect(() => {
    if (isLgDown) {
      setIsCollapsed(true);
      setAnchorEl(null);
      setOpenMenu(null);
      setOpenSubmenus({});
    }
  }, [isLgDown]);

  const prevPathnameRef = useRef(location.pathname);
  useLayoutEffect(() => {
    const didPathnameChange = location.pathname !== prevPathnameRef.current;
    prevPathnameRef.current = location.pathname;

    if (didPathnameChange && isLgDown && !isCollapsed) {
      setIsCollapsed(true);
      setAnchorEl(null);
      setOpenMenu(null);
      setOpenSubmenus({});
    }
  }, [location.pathname, isLgDown, isCollapsed]);

  // On mobile, the drawer body always renders its final/expanded layout —
  // including during the swipe-open drag — so labels, padding and the
  // grabber/close header don't pop in once the drawer fully opens.
  const isMobileExpanded = isMobile;
  const isDisplayCollapsed = isCollapsed && !isMobile;

  const menuStyles = useMemo(() => {
    const containerWidth = isDisplayCollapsed
      ? theme.spacing(8)
      : theme.spacing(32);
    const headerPadding = isDisplayCollapsed ? 0 : isMobileExpanded ? 2.5 : 3;
    const listButtonPx = isDisplayCollapsed ? 1 : isMobileExpanded ? 2.5 : 3;
    const listIconMinWidth = isDisplayCollapsed ? "auto" : theme.spacing(5);
    const flexDirection: "column" | "row" = isDisplayCollapsed
      ? "column"
      : "row";
    const spacing = isDisplayCollapsed ? 1 : 0;

    const shouldFloat = isLgDown && !isCollapsed;

    return {
      container: {
        width: containerWidth,
        flexShrink: 0,
        alignItems: isDisplayCollapsed ? "center" : "flex-start",
        bgcolor: "primary.800",
        color: "common.white",
        boxShadow: shouldFloat ? 8 : 1,
        height: "100vh",
        overflow: "auto",
        pt: isMobileExpanded ? 0 : 2,
        ...(shouldFloat && {
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: theme.zIndex.drawer,
        }),
      },
      header: {
        mb: 2,
        py: headerPadding,
        pl: headerPadding,
        width: "100%",
      },
      listButton: {
        px: listButtonPx,
        py: 1.5,
        justifyContent: isDisplayCollapsed ? "center" : "flex-start",
        color: "common.white",
        "&:hover": {
          bgcolor: alpha(theme.palette.common.white, 0.08),
        },
        "&.Mui-selected": {
          bgcolor: alpha(theme.palette.common.white, 0.12),
        },
      },
      listIcon: {
        minWidth: listIconMinWidth,
        color: "inherit",
      },
      headerStack: {
        flexDirection,
        alignItems: "center" as const,
        justifyContent: "space-between" as const,
        spacing,
      },
      logoImage: {},
      submenuButton: {
        pl: theme.spacing(6),
        color: "common.white",
        "&:hover": {
          bgcolor: alpha(theme.palette.common.white, 0.08),
        },
      },
      collapseButton: {
        borderTopRightRadius: isDisplayCollapsed ? undefined : 0,
        borderBottomRightRadius: isDisplayCollapsed ? undefined : 0,
        bgcolor: "common.white",
        color: "action.active",
        "&:hover": {
          bgcolor: alpha(theme.palette.common.white, 0.88),
        },
      },
    };
  }, [isCollapsed, isDisplayCollapsed, isLgDown, isMobileExpanded, theme]);

  const menuButtonRefs = useRef<Record<string, HTMLElement | null>>({
    clients: null,
  });
  const subMenuButtonRefs = useRef<Record<string, HTMLElement | null>>({
    clients: null,
  });
  const drawerJustOpenedRef = useRef(false);

  const handleMenuMouseEnter = (
    e: MouseEvent<HTMLElement>,
    menuKey: string,
  ) => {
    if (isCollapsed) {
      if (openMenu === menuKey) {
        return;
      }

      const target = e.currentTarget;
      setAnchorEl(target);
      setOpenMenu(menuKey);
    }
  };

  useEffect(() => {
    if (!isCollapsed || !openMenu) {
      return;
    }

    const handleGlobalMouseMove = (e: globalThis.MouseEvent) => {
      try {
        const elements = document.elementsFromPoint(e.clientX, e.clientY);

        const currentMenuButton = menuButtonRefs.current[openMenu];
        const isOverMenuButton =
          currentMenuButton && elements.includes(currentMenuButton);

        const currentSubMenu = subMenuButtonRefs.current[openMenu];
        const isOverSubMenu =
          currentSubMenu && elements.includes(currentSubMenu);

        if (isOverMenuButton || isOverSubMenu) {
          return;
        } else {
          setAnchorEl(null);
          setOpenMenu(null);
        }
      } catch (error) {
        console.error("Error in global mousemove handler:", error);
      }
    };

    document.addEventListener("mousemove", handleGlobalMouseMove);

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
    };
  }, [isCollapsed, openMenu]);

  useEffect(() => {
    if (typeof window === "undefined" || isMobile) {
      return;
    }

    const storageKey = isTablet
      ? MENU_COLLAPSED_STORAGE_KEY_TABLET
      : MENU_COLLAPSED_STORAGE_KEY_DESKTOP;

    try {
      localStorage.setItem(storageKey, encodeStorageValue(isCollapsed));
    } catch (err) {
      console.error("Couldn't set menu collapsed setting:", err);
    }
  }, [isCollapsed, isMobile, isTablet, isDesktop]);

  const toggleCollapse = () => {
    const didCollapse = isCollapsed;
    setIsCollapsed(!didCollapse);
    setAnchorEl(null);
    setOpenMenu(null);
    setOpenSubmenus({});
    if (didCollapse && isMobile) {
      drawerJustOpenedRef.current = true;
      window.setTimeout(() => {
        drawerJustOpenedRef.current = false;
      }, 300);
    }
  };

  const expandMenu = () => {
    if (isCollapsed) {
      toggleCollapse();
    }
  };

  const collapseMenuOnSmallScreen = () => {
    if (isLgDown) {
      setIsCollapsed(true);
      setAnchorEl(null);
      setOpenMenu(null);
      setOpenSubmenus({});
    }
  };

  const collapseIconName = isCollapsed
    ? "keyboard_double_arrow_right"
    : "keyboard_double_arrow_left";

  const clientsSubmenuIconName = openSubmenus.clients
    ? "expand_less"
    : "expand_more";

  const toggleClientsSubmenu = () => {
    setOpenSubmenus((prev) => ({
      ...prev,
      clients: !prev.clients,
    }));
  };

  const handleClientsMouseEnter = (e: MouseEvent<HTMLElement>) => {
    if (isCollapsed) {
      handleMenuMouseEnter(e, "clients");
    }
  };

  const handleSubmenuLinkClick = () => {
    setOpenMenu(null);
    setAnchorEl(null);
  };

  const menuBodyChildren = (
    <>
      {isMobileExpanded ? (
        <Stack sx={{ width: "100%", mb: 2 }}>
          <Stack
            direction="row"
            justifyContent="center"
            sx={{ pt: 1, pb: 1.5 }}
          >
            <PublicSvgIcon name="grabber" />
          </Stack>
          <Stack direction="row" justifyContent="flex-end" sx={{ px: 2.5 }}>
            <IconButton
              onClick={toggleCollapse}
              sx={{ color: "common.white" }}
              data-testid="menu-close-button"
            >
              <PublicSvgIcon name="clear" colorOverride="common.white" />
            </IconButton>
          </Stack>
        </Stack>
      ) : (
        <Stack {...menuStyles.headerStack} sx={menuStyles.header}>
          <Link to="/">
            <Box
              component="img"
              src={
                isDisplayCollapsed ? "/logo_small_white.svg" : "/logo_white.svg"
              }
              alt="Logo"
              sx={menuStyles.logoImage}
            />
          </Link>
          <IconButton
            variant="withBackground"
            onClick={toggleCollapse}
            sx={menuStyles.collapseButton}
            data-testid={
              isDisplayCollapsed ? "menu-expand-button" : "menu-collapse-button"
            }
          >
            <PublicSvgIcon name={collapseIconName} inheritColor />
          </IconButton>
        </Stack>
      )}

      <UserAvatar isCollapsed={isDisplayCollapsed} isMobile={isMobile} />

      {!(isDisplayCollapsed && location.pathname.startsWith("/admin")) && (
        <Stack
          sx={{
            width: isDisplayCollapsed ? "auto" : "100%",
            px: isDisplayCollapsed ? 1 : isMobileExpanded ? 2.5 : 3,
            pb: isDisplayCollapsed ? 0.5 : 2,
          }}
        >
          <ClientSwitcher
            isCollapsed={isDisplayCollapsed}
            onExpand={expandMenu}
            onClientChange={collapseMenuOnSmallScreen}
          />
        </Stack>
      )}

      <List sx={{ width: "100%", pt: 0 }}>
        {shouldShowTransactions && (
          <ListItem disablePadding>
            <ListItemButton
              component={Link as unknown as ElementType}
              {...linkProps}
              sx={menuStyles.listButton}
              data-testid="menu-transactions-link"
            >
              <ListItemIcon sx={menuStyles.listIcon}>
                <PublicSvgIcon name="swap_horiz" colorOverride="common.white" />
              </ListItemIcon>
              {!isDisplayCollapsed && (
                <ListItemText primary="Search Transactions" />
              )}
            </ListItemButton>
          </ListItem>
        )}

        {shouldShowAdminPayouts && (
          <ListItem disablePadding>
            <ListItemButton
              component={Link as unknown as ElementType}
              to="/admin/payouts"
              sx={menuStyles.listButton}
              data-testid="menu-payouts-link"
            >
              <ListItemIcon sx={menuStyles.listIcon}>
                <PublicSvgIcon
                  name="account_balance"
                  colorOverride="common.white"
                />
              </ListItemIcon>
              {!isDisplayCollapsed && <ListItemText primary="Payouts" />}
            </ListItemButton>
          </ListItem>
        )}

        {shouldShowAccountTab && (
          <ListItem disablePadding>
            <ListItemButton
              component={Link as unknown as ElementType}
              to="/clients/$clientId/account"
              params={{ clientId }}
              sx={menuStyles.listButton}
              data-testid="menu-account-link"
            >
              <ListItemIcon sx={menuStyles.listIcon}>
                <PublicSvgIcon name="account" colorOverride="common.white" />
              </ListItemIcon>
              {!isDisplayCollapsed && <ListItemText primary="Account" />}
            </ListItemButton>
          </ListItem>
        )}

        {shouldShowClientSettings && (
          <ListItem disablePadding>
            <ListItemButton
              component={Link as unknown as ElementType}
              to="/clients/$clientId"
              params={{ clientId }}
              sx={menuStyles.listButton}
              data-testid="menu-client-settings-link"
            >
              <ListItemIcon sx={menuStyles.listIcon}>
                <PublicSvgIcon name="settings" colorOverride="common.white" />
              </ListItemIcon>
              {!isDisplayCollapsed && (
                <ListItemText primary="Client Settings" />
              )}
            </ListItemButton>
          </ListItem>
        )}

        {shouldShowClientsMenu && (
          <ListItem disablePadding>
            <ListItemButton
              ref={(el) => {
                menuButtonRefs.current.clients = el;
              }}
              onClick={toggleClientsSubmenu}
              onMouseEnter={handleClientsMouseEnter}
              onMouseOver={handleClientsMouseEnter}
              sx={menuStyles.listButton}
              data-testid="menu-clients-button"
            >
              <ListItemIcon sx={menuStyles.listIcon}>
                <PublicSvgIcon name="group" colorOverride="common.white" />
              </ListItemIcon>
              {!isDisplayCollapsed && <ListItemText primary="Clients" />}
              {!isDisplayCollapsed && (
                <PublicSvgIcon
                  name={clientsSubmenuIconName}
                  colorOverride="common.white"
                />
              )}
              <MuiMenu
                anchorEl={anchorEl}
                open={Boolean(anchorEl) && openMenu === "clients"}
                onClose={() => {
                  setAnchorEl(null);
                  setOpenMenu(null);
                }}
                keepMounted
                disableAutoFocusItem
                disablePortal
                hideBackdrop
                anchorOrigin={{ vertical: "center", horizontal: "right" }}
                transformOrigin={{ vertical: "center", horizontal: "left" }}
                slotProps={{
                  list: {
                    ref: (el: HTMLElement) => {
                      subMenuButtonRefs.current.clients = el;
                    },
                    sx: { py: 0 },
                  },
                  paper: {
                    sx: {},
                  },
                }}
              >
                {canGetClients && (
                  <MenuItem
                    component={Link}
                    to="/admin/clients"
                    onClick={handleSubmenuLinkClick}
                    data-testid="menu-clients-filter-collapsed-link"
                  >
                    Filter Clients
                  </MenuItem>
                )}
                {canCreateClient && (
                  <MenuItem
                    component={Link}
                    to="/admin/clients/create"
                    onClick={handleSubmenuLinkClick}
                    data-testid="menu-clients-create-collapsed-link"
                  >
                    Create Client
                  </MenuItem>
                )}
              </MuiMenu>
            </ListItemButton>
          </ListItem>
        )}

        {shouldShowClientsMenu && !isDisplayCollapsed && (
          <Collapse in={openSubmenus.clients} timeout={0} unmountOnExit>
            <List component="div" disablePadding>
              {canGetClients && (
                <ListItem disablePadding>
                  <ListItemButton
                    component={Link}
                    to="/admin/clients"
                    sx={menuStyles.submenuButton}
                    data-testid="menu-clients-filter-link"
                  >
                    <ListItemText primary="Filter Clients" />
                  </ListItemButton>
                </ListItem>
              )}
              {canCreateClient && (
                <ListItem disablePadding>
                  <ListItemButton
                    component={Link}
                    to="/admin/clients/create"
                    sx={menuStyles.submenuButton}
                    data-testid="menu-clients-create-link"
                  >
                    <ListItemText primary="Create Client" />
                  </ListItemButton>
                </ListItem>
              )}
            </List>
          </Collapse>
        )}

        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              logout();
            }}
            sx={menuStyles.listButton}
            data-testid="menu-logout-button"
          >
            <ListItemIcon sx={menuStyles.listIcon}>
              <PublicSvgIcon name="logout" colorOverride="common.white" />
            </ListItemIcon>
            {!isDisplayCollapsed && <ListItemText primary="Logout" />}
          </ListItemButton>
        </ListItem>
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <Tooltip
        title={isDisplayCollapsed ? "Contact Support" : ""}
        placement="right"
      >
        <ListItem disablePadding>
          <ListItemButton
            component="a"
            href={contactSupportUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={[menuStyles.listButton, { mb: 1.5 }]}
            data-testid="menu-contact-support-link"
          >
            <ListItemIcon sx={menuStyles.listIcon}>
              <PublicSvgIcon
                name="contact_support"
                colorOverride="common.white"
              />
            </ListItemIcon>
            {!isDisplayCollapsed && (
              <ListItemText
                primary="Contact Support"
                slotProps={{ primary: { variant: "body3" } }}
              />
            )}
          </ListItemButton>
        </ListItem>
      </Tooltip>
      <Tooltip
        title={isDisplayCollapsed ? "Submit feedback" : ""}
        placement="right"
      >
        <ListItem disablePadding>
          <ListItemButton
            component="a"
            href={feedbackFormUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={[menuStyles.listButton, { mb: 1.5 }]}
            data-testid="menu-submit-feedback-link"
          >
            <ListItemIcon sx={menuStyles.listIcon}>
              <PublicSvgIcon name="bug_report" colorOverride="common.white" />
            </ListItemIcon>
            {!isDisplayCollapsed && (
              <ListItemText
                primary="Submit Feedback"
                slotProps={{ primary: { variant: "body3" } }}
              />
            )}
          </ListItemButton>
        </ListItem>
      </Tooltip>
      <SystemClock isCollapsed={isDisplayCollapsed} />
    </>
  );

  const menuContent = (
    <Stack
      sx={{
        ...menuStyles.container,
        width: menuStyles.container.width,
      }}
    >
      {menuBodyChildren}
    </Stack>
  );

  const shouldShowOverlay = isLgDown && !isCollapsed;

  const mobileTopBar = (
    <Stack
      direction="row"
      sx={{
        bgcolor: "primary.800",
        py: 2,
        px: 2,
        alignItems: "center",
        position: "relative",
        width: "100%",
        flexShrink: 0,
        zIndex: (t) => t.zIndex.drawer,
      }}
    >
      <IconButton
        onClick={toggleCollapse}
        sx={{
          color: "common.white",
          position: "relative",
          zIndex: 1,
        }}
        data-testid="menu-open-button"
      >
        <PublicSvgIcon name="burger" colorOverride="common.white" />
      </IconButton>
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        }}
      >
        <Link to="/" style={{ pointerEvents: "auto" }}>
          <Box component="img" src="/logo_white.svg" alt="Logo" />
        </Link>
      </Box>
    </Stack>
  );

  if (isMobile) {
    return (
      <>
        {mobileTopBar}
        <SwipeableDrawer
          anchor="left"
          open={!isCollapsed}
          onOpen={() => setIsCollapsed(false)}
          onClose={() => {
            if (drawerJustOpenedRef.current) {
              return;
            }
            setIsCollapsed(true);
          }}
          disableSwipeToOpen={false}
          disableDiscovery={false}
          swipeAreaWidth={60}
          slotProps={{
            paper: {
              sx: {
                width: "80%",
                maxWidth: 320,
                minWidth: 280,
                bgcolor: "primary.800",
                color: "common.white",
                backgroundImage: "none",
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
              },
            },
          }}
        >
          {menuBodyChildren}
        </SwipeableDrawer>
      </>
    );
  }

  return (
    <>
      {shouldShowOverlay && (
        <Box
          onClick={collapseMenuOnSmallScreen}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "blackStates._states.outlinedBorder",
            zIndex: theme.zIndex.drawer - 1,
          }}
        />
      )}
      {menuContent}
    </>
  );
});
